
import * as FEIM from "@rlx/feim";
import App from "../../core/src/App";
import { Commands } from "../../core/src/commands/CommandEnum";
import CommandManager from "../../core/src/commands/CommandManager";
import GameEvents from "../../core/src/const/GameEvents";
import CommManager from "../../core/src/controllers/CommManager";
import InstanceFactory from "../../core/src/factories/InstanceFactory";
import BetModel from "../../core/src/model/BetModel";
import GameModel from "../../core/src/model/GameModel";
import RequestRegister from "../../core/src/server/requests/RequestRegister";
import StateMachine from "../../core/src/states/StateMachine";
import UIController from "../../core/src/ui/UIController";
import { UIStates } from "../../core/src/ui/UIStates";
import ConsoleUtil from "../../core/src/utils/ConsoleUtil";
import SoundController from "../../core/src/utils/SoundController";
import ViewManager from "../../core/src/utils/ViewManager";
import IFeimConfigureDate from "../../src/server/IFEIMConfigureData";
import AutoplayModel from "../../core/src/model/AutoplayModel";
import { VendorCommandsEnum } from "../commands/VendorCommandsEnum";
import { DefaultLayers } from "../../core/src/utils/Layers";


export class RelaxFEIMController {

    private feimAPI: any = FEIM;
    // store prev value and prevent multiple call on updateSettings command
    private soundState: boolean;
    private onLogs: boolean = true;


    public initializeAPI() {
        if (this.onLogs) {
            ConsoleUtil.red("[Relax FEIM API] Initialize");
    
        }
        this.setupEventListeners();
    }

    public configure(data: IFeimConfigureDate): void
    public configure(data: any): void {
        if (this.onLogs) {
            ConsoleUtil.red("[Relax FEIM API] Configure");
        }
        let configData = this.getFeimConfigData(data);
        this.feimAPI.configure(configData)
    }

    private setupEventListeners() {
        this.feimAPI.on.freeze((event) => {
            if (this.onLogs) {
                ConsoleUtil.red("[RelaxPostMessage] onPause()", event);
            }
            this.setPauseOn(event);
            ViewManager.getLayer(DefaultLayers.PauseOverlay).setScene("open").interactive = true;
        });

        this.feimAPI.on.unfreeze((event) => {
            if (this.onLogs) {
                ConsoleUtil.red("[RelaxPostMessage] onResume()", event);
            }
            this.setPauseOff();
            ViewManager.getLayer(DefaultLayers.PauseOverlay).clear();
        });

        this.feimAPI.on.togglePaytable((action) => {
            if (this.onLogs) {
                ConsoleUtil.red("[RelaxPostMessage] togglePaytable", action);
            }
            const showPT = !(action?.type == "show"); 
            if(UIController.isComponentHidden("settings") || showPT) {
                CommandManager.execute(Commands.TOGGLE_PAYTABLE);
            }
        });

        this.feimAPI.on.toggleGameHelp((action) => {
            if (this.onLogs) {
                ConsoleUtil.red("[RelaxPostMessage] toggleGameHelp", action);
            }
            const showPT = !(action?.type == "show"); 
            if(UIController.isComponentHidden("settings") || showPT) {
                CommandManager.execute(VendorCommandsEnum.TOGGLE_GAME_INFO);
            }
        });

        this.feimAPI.on.pauseAutoPlay(() => {
            if (this.onLogs) {
                ConsoleUtil.red("[RelaxPostMessage] pauseAutoPlay");
            }
            if(AutoplayModel.isAutoSpinActive()) {
                App.instance.paused = true;
            }
        });

        this.feimAPI.on.updateSettings((settings: any) => {
            console.warn('Update sound settings using event listener');
            this.changeSound(settings.sounds)
            this.reportSound();
        })

        this.feimAPI.on.refreshBalance(() => {
            GameModel.shouldUpdateBalance = true;
            if (StateMachine.currentState.name == "idle") {
                UIController.setState(UIStates.SPIN);
                CommManager.send(RequestRegister.get("getBalance"), (data) => {
                    BetModel.balance = data.b;
                    UIController.setState(UIStates.IDLE);
                    this.balanceUpdate(data.b);
                }, this);
            }
        })

        App.dispatcher.on(GameEvents.SOUNDS_UNMUTED, () => {
            this.reportSound();
        }, this);

        App.dispatcher.on(GameEvents.SOUNDS_MUTED, () => {
            this.reportSound();
        });

        this.feimAPI.on.exitingGame((event) => {
            if (this.onLogs) {
                ConsoleUtil.red("[RelaxPostMessage] exitingGame", event);
            }
        });
        
    }

    private setPauseOn(event: any) {
        App.instance.paused = true;
    }
    
    private setPauseOff() {
        App.instance.paused = false;
    }

    public gameLoadStarted(): void {
        if (this.onLogs) {
            ConsoleUtil.red("[Relax FEIM API] Game Load Started");
    
        }
        this.feimAPI.send.gameLoadStarted();
    }

    public gameLoadProgress(progress: number): void {
        if (this.onLogs) {
            ConsoleUtil.red("[Relax FEIM API] Game Load Progress");
        }
        this.feimAPI.send.gameLoadProgress(progress);
    }

    public gameLoadCompleted(): void {
        if (this.onLogs) {
            ConsoleUtil.red("[Relax FEIM API] Game Load Complete");
        }
        this.feimAPI.send.gameLoadCompleted();
    }

    public balanceUpdate(balance: number) {
        if (this.onLogs) {
            ConsoleUtil.red("[Relax FEIM API] Balance Update");
    
        }
        this.feimAPI.send.balanceUpdate(balance);
    }

    public autoPlayStarted() {
        if (this.onLogs) {
            ConsoleUtil.red("[Relax FEIM API] Autoplay Start");
    
        }
        this.feimAPI.send.autoPlayStarted(); 
    }

    public autoPlayFinished() {
        if (this.onLogs) {
            ConsoleUtil.red("[RelaxPostMessage FEIM API] Autoplay Finish");
    
        }
        this.feimAPI.send.autoPlayFinished();
    }

    public winUpdate(winValue: number) {
        if (this.onLogs) {
            ConsoleUtil.red("[RelaxPostMessage FEIM API] Win Update");
        }
        this.feimAPI.send.winUpdate({ win: winValue });
    }

    public betUpdate(bet: number) {
        if (this.onLogs) {
            ConsoleUtil.red("[Relax FEIM API] Bet Update");
        }
        this.feimAPI.send.betUpdate(bet);
    }

    public getFeimConfigData(data: IFeimConfigureDate): IFeimConfigureDate {
        let result = {};
    
        data?.clientconfigResponse ? result["clientConfig"] = data.clientconfigResponse : null;
        data?.loginResponse ? result["loginResponse"] = data.loginResponse : null;
        data?.p2pConfig ? result["p2pConfig"] = data.p2pConfig : null;
        data?.handleRgPostMessageAPI ? result["handleRgPostMessageAPI"] = data.handleRgPostMessageAPI : result["handleRgPostMessageAPI"] = true;
        data?.handleFeaturePause ? result["handleFeaturePause"] = data.handleFeaturePause : result["handleFeaturePause"] = false;
        data?.showErrorMessageCallback ? result["showErrorMessageCallback"] = data.showErrorMessageCallback : null;
    
        return result
    }

    public dreamDropEnded() {
        this.feimAPI.send.jackpotResolved();
        if (this.onLogs) {
            ConsoleUtil.red("[Relax FEIM API] Dream Drop Ended");
        }
    }

    public gameRoundStarted(balance) {
        this.feimAPI.send.roundStarted(balance);
        if (this.onLogs) {
            ConsoleUtil.red("[Relax FEIM API] Round Start");
        }
    }

    public gameRoundEnded(balance) {
        this.feimAPI.send.roundFinished(balance);
        if (this.onLogs) {
            ConsoleUtil.red("[Relax FEIM API] Round End");
        }
    }

    public reportSound() {
        if (this.onLogs) {
            ConsoleUtil.red("[Relax FEIM API] Report Sound");
    
        }
        if (this.soundState != SoundController.isSoundEnabled) {
            this.feimAPI.send.updateSettings({ sounds: SoundController.isSoundEnabled });
            this.soundState = SoundController.isSoundEnabled;
        }
    }

    public sendUserAction(actionUrl: string, inNewWindow: boolean = false) {
        if (this.onLogs) {
            ConsoleUtil.red("[Relax FEIM API] User Action");
        }

        if (!inNewWindow) {
            this.feimAPI.send.navigate(actionUrl);
        } else {
            window.open(actionUrl, "_blank");
        }
    }

    public showHistory() {
        this.feimAPI.send.showHistory();
    }

    private changeSound(enableSounds: boolean) {
        if((enableSounds && (!SoundController.isMusicEnabled || !SoundController.isSoundEnabled)) ||
            !enableSounds && (SoundController.isMusicEnabled || SoundController.isSoundEnabled)
        ) {
            CommandManager.execute(Commands.TOGGLE_MASTER_SOUND);
            CommandManager.execute(VendorCommandsEnum.TOGGLE_SOUND_BUTTON);
            CommandManager.execute(Commands.SAVE_SETTINGS);
        }
        
        if (this.onLogs) {
            ConsoleUtil.red("[Relax FEIM API] Sound Change");
        }
    }

    public fastPlayActivated(value: boolean) {
        this.feimAPI.send.updateSettings({ fastPlay: value });
    }

    public featureStarted(): void {
        if (this.onLogs) {
            ConsoleUtil.red("[Relax FEIM API] Feature Start");
        }
        this.feimAPI.send.featureStarted();
    }

    public featureFinished(): void {
        if (this.onLogs) {
            ConsoleUtil.red("[Relax FEIM API] Feature Finish");
        }
        this.feimAPI.send.featureFinished();
    }
}

export default new RelaxFEIMController();